import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { IconSearch } from "@tabler/icons-react";
import "./billing.scss";
import { useClinicData } from "../../context/ClinicDataContext";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import RecordPaymentModal from "../../components/RecordPaymentModal/RecordPaymentModal";
import { BASE_PATH } from "../../components/AppLayout/AppLayout";
import { formatDate, formatRM } from "../../utils/format";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "open", label: "Unpaid" },
  { id: "overdue", label: "Overdue" },
  { id: "paid", label: "Paid" },
];

const BillingPage = () => {
  const { invoices, today, patientById, itemName, invoiceTotal, invoiceStatus } = useClinicData();
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [payInvoice, setPayInvoice] = useState(null);

  const enriched = useMemo(
    () =>
      invoices
        .map((inv) => {
          const total = invoiceTotal(inv);
          return { ...inv, total, balance: total - inv.paid, status: invoiceStatus(inv) };
        })
        .sort((a, b) => b.date.localeCompare(a.date)),
    [invoices, invoiceTotal, invoiceStatus]
  );

  const matches = (inv, id) =>
    id === "all" ||
    (id === "open" && (inv.status === "unpaid" || inv.status === "partial")) ||
    inv.status === id;

  const rows = enriched.filter((inv) => {
    const q = query.trim().toLowerCase();
    const patient = patientById[inv.patientId];
    if (q && !`${inv.id} ${patient?.name}`.toLowerCase().includes(q)) return false;
    return matches(inv, filter);
  });

  const month = today.slice(0, 7);
  const summary = [
    { label: "Outstanding", value: enriched.reduce((s, inv) => s + Math.max(inv.balance, 0), 0) },
    {
      label: "Overdue",
      value: enriched.filter((inv) => inv.status === "overdue").reduce((s, inv) => s + inv.balance, 0),
      tone: "danger",
    },
    {
      label: "Billed this month",
      value: enriched.filter((inv) => inv.date.startsWith(month)).reduce((s, inv) => s + inv.total, 0),
    },
    {
      label: "Collected this month",
      value: enriched.filter((inv) => inv.date.startsWith(month)).reduce((s, inv) => s + Math.min(inv.paid, inv.total), 0),
    },
  ];

  return (
    <div className="billing-page">
      <section className="billing-page__summary" aria-label="Billing summary">
        {summary.map((s) => (
          <div className="billing-page__figure" key={s.label}>
            <span>{s.label}</span>
            <strong className={s.tone === "danger" && s.value > 0 ? "billing-page__danger" : ""}>
              {formatRM(s.value)}
            </strong>
          </div>
        ))}
      </section>

      <div className="toolbar">
        <div className="toolbar__group">
          <div className="search">
            <IconSearch className="search__icon" size={18} stroke={1.75} />
            <label className="visually-hidden" htmlFor="invoice-search">
              Search invoices
            </label>
            <input
              id="invoice-search"
              className="search__input"
              type="search"
              placeholder="Search invoices"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="tabs" role="group" aria-label="Filter invoices">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`tabs__item ${filter === f.id ? "tabs__item--active" : ""}`}
                aria-pressed={filter === f.id}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
                <span className="tabs__count">{enriched.filter((inv) => matches(inv, f.id)).length}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="panel panel--flush">
        <div className="data-table__wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Patient</th>
                <th>Issued</th>
                <th>Due</th>
                <th className="data-table__num">Total</th>
                <th className="data-table__num">Balance</th>
                <th>Status</th>
                <th>
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((inv) => {
                const patient = patientById[inv.patientId];
                return (
                  <tr key={inv.id}>
                    <td>
                      <span className="data-table__primary">{inv.id}</span>
                      <span className="data-table__secondary">
                        {inv.items.map(itemName).join(", ")}
                      </span>
                    </td>
                    <td className="data-table__nowrap">
                      {patient ? (
                        <Link className="billing-page__patient" to={`${BASE_PATH}/patients/${patient.id}`}>
                          {patient.name}
                        </Link>
                      ) : (
                        "Walk-in customer"
                      )}
                    </td>
                    <td className="data-table__nowrap">{formatDate(inv.date)}</td>
                    <td className="data-table__nowrap">{formatDate(inv.dueDate)}</td>
                    <td className="data-table__num">{formatRM(inv.total)}</td>
                    <td className="data-table__num">
                      {inv.balance > 0 ? <strong>{formatRM(inv.balance)}</strong> : "—"}
                      {inv.paid > 0 && inv.method && (
                        <span className="data-table__secondary">Paid by {inv.method}</span>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={inv.status} type="invoice" />
                    </td>
                    <td className="data-table__num">
                      {inv.status !== "paid" && (
                        <button className="btn btn--secondary btn--sm" type="button" onClick={() => setPayInvoice(inv)}>
                          Record payment
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && (
            <div className="empty-state">
              <p className="empty-state__title">No invoices here</p>
              <p>Invoices you issue after a visit will show up in this list.</p>
            </div>
          )}
        </div>
      </div>

      {payInvoice && (
        <RecordPaymentModal
          invoice={invoices.find((inv) => inv.id === payInvoice.id)}
          onClose={() => setPayInvoice(null)}
        />
      )}
    </div>
  );
};

export default BillingPage;
